import * as React from 'react'
import cx from 'classnames'
import { observer } from 'mobx-react'
import { Sandwich, SandwichContent } from 'components/Sandwich'
import { EmptyState, EmptyStateIllustrationImage } from 'components/EmptyState'
import { PayoutDraftingViewModel } from 'payouts/admin/review/PayoutDraftingViewModel'
import ContentLoading from 'components/ContentLoading'
import ContentError from 'components/ContentError'
import { IconButton } from 'components/IconButton'
import Icon from 'components/Icon'
import {
  Dropdown,
  DropdownItem,
  DropdownItemTitle,
  DropdownTitle,
} from 'components/Dropdown'
import Button from 'components/Button'
import ScrollShadow from 'components/ScrollShadow'
import { useScrollOffsetSpring } from 'hooks/spring-hooks'
import { clamp } from 'utils/math-util'
import ApprovedTransaction from './ApprovedTransaction'
import { ConfirmPayoutModal } from './ConfirmPayoutModal'
import styles from './DraftingView.sass'
import { Amount } from 'payouts/components/Amount'

interface DraftingViewProps {
  vm: PayoutDraftingViewModel
}

/**
 * The DraftingView
 */
export default observer(function DraftingView({ vm }: DraftingViewProps) {
  return (
    <Sandwich stretch>
      <SandwichContent stretch>
        <div className={cx(styles.root)}>
          {vm.fetchCurrentDraft.match({
            pending: () => <ContentLoading />,
            rejected: (err: any) => <ContentError error={err} />,
            resolved: () =>
              vm.approvedTransactions.length === 0 ? (
                <EmptyDraft />
              ) : (
                <Draft vm={vm} />
              ),
          })}
        </div>
      </SandwichContent>
    </Sandwich>
  )
})

/**
 * The draft.
 */
const Draft = observer(function Draft({ vm }: { vm: PayoutDraftingViewModel }) {
  const DELTA = 48 // Used to animate the scroll shadow
  const scrollRef = React.useRef<HTMLDivElement | null>(null)
  const scrollProgress = useScrollOffsetSpring(scrollRef).to((v) =>
    clamp(v / DELTA),
  )

  return (
    <div className={styles.draft}>
      <div className={styles.content}>
        <div className={styles.card}>
          <div className={styles.headerContainer}>
            <div className={styles.header}>
              <div className={styles.title}>
                Approved transactions
                <span className={styles.approvedCount}>
                  ({vm.approvedTransactions.length})
                </span>
              </div>
              <div className={styles.headerActions}>
                <Dropdown
                  autoDismiss
                  disabled={vm.tasks.pending}
                  button={
                    <IconButton disabled={vm.tasks.pending}>
                      <Icon name="more" />
                    </IconButton>
                  }
                >
                  <DropdownTitle>Actions</DropdownTitle>
                  <DropdownItem
                    onClick={vm.exportCsv}
                    icon={<Icon name="download" />}
                  >
                    <DropdownItemTitle>Download CSV</DropdownItemTitle>
                  </DropdownItem>
                  <DropdownItem
                    icon={<Icon name="close" />}
                    onClick={vm.removeAll}
                  >
                    <DropdownItemTitle>Remove all</DropdownItemTitle>
                  </DropdownItem>
                </Dropdown>
              </div>
            </div>
            <ScrollShadow lift borderless opacity={scrollProgress} />
          </div>

          <div className={styles.transactions} ref={scrollRef}>
            {vm.approvedTransactions.map((avm) => (
              <ApprovedTransaction
                disabled={vm.tasks.pending}
                key={avm.transaction.id}
                vm={avm}
              />
            ))}
          </div>
        </div>
      </div>

      <div className={styles.footer}>
        <div className={styles.summary}>
          <div className={styles.summaryTitle}>Total</div>
          <div className={cx(styles.summaryValue)}>
            <Amount value={vm.total} />
          </div>
        </div>
        <div className={styles.footerActions}>
          <Button
            block
            size="large"
            type="primary"
            disabled={!vm.canSubmit}
            loading={vm.submit.pending}
            onClick={vm.confirmSubmitVM.show}
          >
            Submit Payout
          </Button>
        </div>
      </div>
      <ConfirmPayoutModal vm={vm} />
    </div>
  )
})

/**
 * Empty Draft
 */
function EmptyDraft() {
  return (
    <div className={styles.emptyStateContainer}>
      <div className={styles.emptyState}>
        <EmptyState
          title="No approved transactions"
          subtitle="There are no approved transactions. Use the table on the left to add some!"
          small
          image={
            <EmptyStateIllustrationImage
              src={require('assets/images/img_missing-roles.svg')}
            />
          }
        />
      </div>
    </div>
  )
}
